import type { JobItem } from '@/api/types';
import { redactSecretsInText } from './redactSecrets';

export function isFailedJobItemStatus(status: string): boolean {
  return status === 'failed' || status === 'dead_letter';
}

export function formatJobItemError(error: string | null | undefined): string {
  if (!error?.trim()) return '—';
  return redactSecretsInText(error.trim());
}

export function jobItemSourceName(item: JobItem): string | null {
  const name = item.payload?.source_name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

export function jobItemRecipientLabel(item: JobItem): string | null {
  const channel = item.payload?.channel;
  const chatId = item.payload?.chat_id;
  const channelLabel = typeof channel === 'string' && channel ? channel : 'telegram';
  if (typeof chatId === 'string' && chatId.trim()) {
    return `${channelLabel} · ${chatId.trim()}`;
  }
  return null;
}

export type FailureCountRow = { label: string; count: number };

export type SendJobFailureSummary = {
  total: number;
  byError: FailureCountRow[];
  bySource: FailureCountRow[];
  byRecipient: FailureCountRow[];
};

function toSortedRows(map: Map<string, number>): FailureCountRow[] {
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function buildSendJobFailureSummary(items: JobItem[]): SendJobFailureSummary | null {
  const failed = items.filter((item) => isFailedJobItemStatus(item.status));
  if (!failed.length) return null;

  const byError = new Map<string, number>();
  const bySource = new Map<string, number>();
  const byRecipient = new Map<string, number>();

  for (const item of failed) {
    const errorLabel = formatJobItemError(item.error) || 'Unknown error';
    byError.set(errorLabel, (byError.get(errorLabel) ?? 0) + 1);

    const source = jobItemSourceName(item) ?? 'Unknown source';
    bySource.set(source, (bySource.get(source) ?? 0) + 1);

    const recipient = jobItemRecipientLabel(item) ?? 'Unknown recipient';
    byRecipient.set(recipient, (byRecipient.get(recipient) ?? 0) + 1);
  }

  return {
    total: failed.length,
    byError: toSortedRows(byError),
    bySource: toSortedRows(bySource),
    byRecipient: toSortedRows(byRecipient),
  };
}

export function formatJobItemPayloadSummary(item: JobItem): Array<{ label: string; value: string }> {
  const p = item.payload ?? {};
  const rows: Array<{ label: string; value: string }> = [];
  const push = (label: string, key: string) => {
    const raw = p[key];
    if (raw == null || raw === '') return;
    rows.push({ label, value: String(raw) });
  };
  push('Source', 'source_name');
  push('Channel', 'channel');
  push('Chat ID', 'chat_id');
  push('Discovered link', 'discovered_link_id');
  push('Subscription', 'subscription_id');
  push('URL', 'url');
  push('Title', 'title');
  return rows;
}
